<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-chart-line me-2 text-primary"></i>
            {{ $t('riepilogoAndamento') || 'Andamento Economico & Marginalità' }}
          </h1>
          <p class="sci-subtitle">Analisi costi delle materie prime, ricavi complessivi e utile netto operativo</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button class="sci-btn sci-btn-primary" @click="avviaAnalisi" :disabled="isLoading">
          <i class="fas fa-calculator me-1" :class="{ 'fa-spin': isLoading }"></i>
          <span>{{ isLoading ? 'Calcolo in corso...' : 'Avvia Analisi' }}</span>
        </button>
      </div>
    </header>

    <!-- Main Spatial Bento Grid -->
    <main class="sci-main-content">
      <!-- Date Range & Period Filter Bento Panel -->
      <section class="sci-card filter-panel mb-4">
        <div class="filter-header">
          <div class="filter-title-group">
            <h2 class="filter-title">
              <i class="fas fa-calendar-alt me-2 text-primary"></i>
              Intervallo Temporale di Analisi
            </h2>
            <span class="filter-caption">Seleziona il periodo desiderato o usa una scorciatoia rapida</span>
          </div>

          <!-- Quick Period Presets -->
          <div class="quick-presets">
            <button 
              type="button" 
              class="preset-pill" 
              :class="{ active: currentPreset === 'today' }" 
              @click="setPreset('today')"
            >
              Oggi
            </button>
            <button 
              type="button" 
              class="preset-pill" 
              :class="{ active: currentPreset === 'week' }" 
              @click="setPreset('week')"
            >
              Ultimi 7 Giorni
            </button>
            <button 
              type="button" 
              class="preset-pill" 
              :class="{ active: currentPreset === 'month' }" 
              @click="setPreset('month')"
            >
              Questo Mese
            </button>
            <button 
              type="button" 
              class="preset-pill" 
              :class="{ active: currentPreset === 'last30' }" 
              @click="setPreset('last30')"
            >
              Ultimi 30 Giorni
            </button>
            <button 
              type="button" 
              class="preset-pill" 
              :class="{ active: currentPreset === 'year' }" 
              @click="setPreset('year')"
            >
              Anno Corrente
            </button>
          </div>
        </div>

        <div class="filter-body">
          <div class="calendar-wrapper">
            <v-date-picker 
              v-model="range" 
              is-range 
              mode="date" 
              :columns="2"
              class="sci-date-picker"
            />
          </div>
          <div class="range-summary">
            <div class="summary-box">
              <span class="summary-label">Data Inizio</span>
              <span class="summary-val font-monospace">{{ formatDate(range.start) }}</span>
            </div>
            <div class="summary-arrow">
              <i class="fas fa-arrow-right"></i>
            </div>
            <div class="summary-box">
              <span class="summary-label">Data Fine</span>
              <span class="summary-val font-monospace">{{ formatDate(range.end) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- KPI Bento Row -->
      <section class="kpi-bento-grid mb-4">
        <!-- 1: Ricavo Totale -->
        <div class="sci-card kpi-card kpi-revenue">
          <div class="kpi-icon-box revenue-icon">
            <i class="fas fa-wallet"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Ricavo Complessivo</span>
            <span class="kpi-number font-monospace">{{ formatCurrency(incasso) }} €</span>
            <span class="kpi-subtext text-muted">Totale fatturato nel periodo</span>
          </div>
        </div>

        <!-- 2: Costo Materie Prime -->
        <div class="sci-card kpi-card kpi-expense">
          <div class="kpi-icon-box expense-icon">
            <i class="fas fa-shopping-basket"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Costo Materie Prime</span>
            <span class="kpi-number font-monospace">{{ formatCurrency(spesa) }} €</span>
            <span class="kpi-subtext text-muted">Spesa ingredienti associati</span>
          </div>
        </div>

        <!-- 3: Guadagno / Utile Netto -->
        <div class="sci-card kpi-card" :class="guadagno >= 0 ? 'kpi-profit' : 'kpi-loss'">
          <div class="kpi-icon-box" :class="guadagno >= 0 ? 'profit-icon' : 'loss-icon'">
            <i class="fas" :class="guadagno >= 0 ? 'fa-chart-line' : 'fa-arrow-down'"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Margine Operativo Lordo</span>
            <span class="kpi-number font-monospace" :class="guadagno >= 0 ? 'text-success' : 'text-danger'">
              {{ guadagno >= 0 ? '+' : '' }}{{ formatCurrency(guadagno) }} €
            </span>
            <span class="kpi-subtext" :class="guadagno >= 0 ? 'text-success' : 'text-danger'">
              {{ guadagno >= 0 ? 'Saldo positivo' : 'Disavanzo registrato' }}
            </span>
          </div>
        </div>

        <!-- 4: Indice di Marginalità % -->
        <div class="sci-card kpi-card kpi-margin">
          <div class="kpi-icon-box margin-icon">
            <i class="fas fa-percentage"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Incidenza Marginalità</span>
            <span class="kpi-number font-monospace">{{ marginalita.toFixed(1) }}%</span>
            <span class="kpi-subtext text-muted">Resa su ricavo totale</span>
          </div>
        </div>
      </section>

      <!-- Financial Breakdown Card -->
      <section class="sci-card mb-4" v-if="hasRunAnalysis">
        <div class="breakdown-header">
          <h3 class="breakdown-title">
            <i class="fas fa-balance-scale me-2 text-primary"></i>
            Ripartizione Economica Incasso vs Costi
          </h3>
          <span class="badge-status" :class="guadagno >= 0 ? 'badge-positive' : 'badge-negative'">
            {{ guadagno >= 0 ? 'Margine Sostenibile' : 'Attenzione Costi Eccessivi' }}
          </span>
        </div>

        <div class="breakdown-body">
          <!-- Ratio Bar -->
          <div class="ratio-bar-wrap">
            <div 
              class="ratio-segment segment-expense" 
              :style="{ width: expenseRatio + '%' }"
              :title="'Spesa: ' + expenseRatio.toFixed(1) + '%'"
            >
              <span v-if="expenseRatio > 15">{{ expenseRatio.toFixed(1) }}% Costo</span>
            </div>
            <div 
              class="ratio-segment segment-profit" 
              :style="{ width: profitRatio + '%' }"
              :title="'Margine: ' + profitRatio.toFixed(1) + '%'"
            >
              <span v-if="profitRatio > 15">{{ profitRatio.toFixed(1) }}% Margine</span>
            </div>
          </div>

          <div class="ratio-legend">
            <div class="legend-item">
              <span class="legend-dot dot-expense"></span>
              <span>Costo Materie Prime: <strong>{{ formatCurrency(spesa) }} €</strong> ({{ expenseRatio.toFixed(1) }}%)</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot dot-profit"></span>
              <span>Margine Operativo: <strong>{{ formatCurrency(guadagno) }} €</strong> ({{ profitRatio.toFixed(1) }}%)</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Empty State before analysis -->
      <section class="sci-card empty-analysis-card" v-if="!hasRunAnalysis && !isLoading">
        <div class="empty-content text-center py-5">
          <div class="empty-icon-halo">
            <i class="fas fa-chart-pie"></i>
          </div>
          <h3 class="empty-title">Nessuna analisi generata per l'intervallo corrente</h3>
          <p class="empty-desc">
            Seleziona le date desiderate e fai clic su <strong>"Avvia Analisi"</strong> per elaborare i dati finanziari del ristorante.
          </p>
          <button class="sci-btn sci-btn-primary mt-3" @click="avviaAnalisi">
            <i class="fas fa-play me-1"></i> Calcola Ora
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import moment from 'moment'
import { store } from '../store/store.js'

export default {
  name: 'RiepilogoAndamento',
  data() {
    return {
      incasso: 0,
      spesa: 0,
      range: {
        start: moment().startOf('month').toDate(),
        end: moment().endOf('month').toDate()
      },
      currentPreset: 'month',
      isLoading: false,
      hasRunAnalysis: false,
      store
    }
  },
  computed: {
    guadagno() {
      const inc = parseFloat(this.incasso) || 0;
      const spe = parseFloat(this.spesa) || 0;
      return inc - spe;
    },
    marginalita() {
      const inc = parseFloat(this.incasso) || 0;
      if (inc <= 0) return 0;
      return (this.guadagno / inc) * 100;
    },
    expenseRatio() {
      const inc = parseFloat(this.incasso) || 0;
      const spe = parseFloat(this.spesa) || 0;
      if (inc <= 0 && spe <= 0) return 0;
      const base = Math.max(inc, spe);
      return Math.min(100, Math.max(0, (spe / base) * 100));
    },
    profitRatio() {
      const exp = this.expenseRatio;
      return Math.max(0, 100 - exp);
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    formatDate(d) {
      if (!d) return '--/--/----';
      return moment(d).format('DD/MM/YYYY');
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    setPreset(preset) {
      this.currentPreset = preset;
      if (preset === 'today') {
        this.range = {
          start: moment().startOf('day').toDate(),
          end: moment().endOf('day').toDate()
        };
      } else if (preset === 'week') {
        this.range = {
          start: moment().subtract(6, 'days').startOf('day').toDate(),
          end: moment().endOf('day').toDate()
        };
      } else if (preset === 'month') {
        this.range = {
          start: moment().startOf('month').toDate(),
          end: moment().endOf('month').toDate()
        };
      } else if (preset === 'last30') {
        this.range = {
          start: moment().subtract(29, 'days').startOf('day').toDate(),
          end: moment().endOf('day').toDate()
        };
      } else if (preset === 'year') {
        this.range = {
          start: moment().startOf('year').toDate(),
          end: moment().endOf('year').toDate()
        };
      }
      this.avviaAnalisi();
    },
    avviaAnalisi() {
      this.isLoading = true;
      const start = moment(this.range.start || new Date()).startOf('day').unix();
      const end = moment(this.range.end || new Date()).endOf('day').unix();

      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + 'getAnalisi', {
        start: start,
        end: end,
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        if (response.data) {
          this.incasso = parseFloat(response.data.incasso) || 0;
          this.spesa = parseFloat(response.data.spesa) || 0;
          this.hasRunAnalysis = true;
        }
      }).catch(err => {
        console.error("Errore recupero analisi costi:", err);
      }).finally(() => {
        this.isLoading = false;
      });
    }
  },
  mounted() {
    this.avviaAnalisi();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.sci-btn-primary {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 20px;
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-primary:hover:not(:disabled) {
  background: #4338ca;
}

.sci-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sci-main-content {
  max-width: 1300px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 20px;
}

/* Card */
.sci-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 18px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
  padding: 22px;
}

/* Filter Panel */
.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.filter-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
}

.filter-caption {
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.quick-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.preset-pill {
  min-height: 38px;
  padding: 0 14px;
  background: #f8fafc;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  cursor: pointer;
  transition: all 0.2s;
}

.preset-pill:hover {
  background: #eef2ff;
  color: var(--sci-primary, #4f46e5);
  border-color: #c7d2fe;
}

.preset-pill.active {
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
  border-color: var(--sci-primary, #4f46e5);
}

.filter-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.range-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f8fafc;
  padding: 16px 20px;
  border-radius: 14px;
  border: 1px solid var(--sci-border, #e2e8f0);
}

.summary-box {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--sci-text-muted, #475569);
}

.summary-val {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--sci-text-main, #0f172a);
}

.summary-arrow {
  color: var(--sci-text-muted, #475569);
}

/* KPI Grid */
.kpi-bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
}

.kpi-icon-box {
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
}

.revenue-icon {
  background: #eef2ff;
  color: #4f46e5;
}

.expense-icon {
  background: #fff7ed;
  color: #ea580c;
}

.profit-icon {
  background: #ecfdf5;
  color: #059669;
}

.loss-icon {
  background: #fff1f2;
  color: #e11d48;
}

.margin-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--sci-text-muted, #475569);
  letter-spacing: 0.4px;
}

.kpi-number {
  font-size: 1.45rem;
  font-weight: 800;
  margin: 3px 0 2px 0;
}

.kpi-subtext {
  font-size: 0.78rem;
}

/* Ratio Bar */
.breakdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.breakdown-title {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
}

.badge-status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
}

.badge-positive {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.badge-negative {
  background: #fff1f2;
  color: #9f1239;
  border: 1px solid #fecdd3;
}

.ratio-bar-wrap {
  display: flex;
  height: 28px;
  background: #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
}

.ratio-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 700;
  transition: width 0.4s ease;
}

.segment-expense {
  background: #f97316;
}

.segment-profit {
  background: #10b981;
}

.ratio-legend {
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  font-size: 0.88rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.dot-expense { background: #f97316; }
.dot-profit { background: #10b981; }

.empty-icon-halo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #eef2ff;
  color: var(--sci-primary, #4f46e5);
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
}

.empty-title {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.empty-desc {
  font-size: 0.88rem;
  color: var(--sci-text-muted, #475569);
  max-width: 500px;
  margin: 0 auto;
}
</style>
